import { motion } from "framer-motion";
import { Phone, CalendarCheck, FileText, Star, ShieldCheck, Car, Clock, TrendingUp } from "lucide-react";
import { useModal } from "../context/ModalContext";
import { useSettings } from "../context/SettingsContext";
import { WhatsAppButton } from "../components/WhatsAppButton";

const HERO_IMG = "https://images.pexels.com/photos/6817037/pexels-photo-6817037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1000";

const BADGES = [
  { icon: ShieldCheck, label: "DVSA Approved" },
  { icon: Car, label: "Manual & Automatic" },
  { icon: Clock, label: "Flexible Timings" },
  { icon: TrendingUp, label: "98% Pass Rate" },
];

export function Hero() {
  const { open } = useModal();
  const { settings } = useSettings();

  return (
    <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="hero-section">
      <div className="absolute top-20 -right-32 w-[36rem] h-[36rem] rounded-full brand-gradient opacity-[0.10] blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-brand-amber opacity-[0.08] blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="overline">Skills · Safety · Success</span>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-100 px-4 py-1.5 text-xs font-semibold text-brand-red">
              <Star size={13} className="fill-brand-amber text-brand-amber" /> Rated 4.9/5 by 320+ learners
            </div>
          </motion.div>

          <motion.h1
            className="mt-6 font-heading font-extrabold tracking-tighter text-neutral-900 text-[clamp(2.5rem,11vw,3.75rem)] lg:text-[clamp(3.75rem,5.2vw,4.5rem)] leading-[1.02]"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
          >
            Driving Lessons in Northolt <span className="brand-text">with Confidence</span>
          </motion.h1>

          <motion.p
            className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
          >
            We provide manual and automatic driving lessons across Northolt, Greenford, Southall and South Harrow.
          </motion.p>

          <motion.p
            className="mt-4 inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl sm:rounded-full bg-neutral-900 text-white text-sm font-semibold px-4 py-2"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          >
            🚗 Lessons from <span className="brand-text font-extrabold">£14.95</span> · Free Theory Test Tuition
          </motion.p>

          <motion.div
            className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18 }}
          >
            <button className="btn-primary w-full sm:w-auto" onClick={() => open("booking")} data-testid="hero-book-btn"><CalendarCheck size={18} /> Book Lesson</button>
            <button className="btn-dark w-full sm:w-auto" onClick={() => open("quote")} data-testid="hero-quote-btn"><FileText size={18} /> Get Free Quote</button>
            <a href={`tel:${settings.phone_link || ""}`} className="btn-outline w-full sm:w-auto" data-testid="hero-call-btn"><Phone size={18} /> Call Now</a>
            <WhatsAppButton label="WhatsApp Us" className="w-full sm:w-auto !px-5 !py-[0.8rem]" testId="hero-whatsapp-btn" />
          </motion.div>

          <motion.div
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.28 }}
          >
            {BADGES.map((b) => (
              <div key={b.label} className="flex items-center gap-2 rounded-2xl bg-white border border-neutral-100 shadow-soft px-3 py-3">
                <b.icon size={20} className="text-brand-orange shrink-0" strokeWidth={1.8} />
                <span className="text-xs font-semibold text-neutral-700 leading-tight">{b.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-6 relative"
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-soft-lg">
            <img src={HERO_IMG} alt="DVSA-approved instructor teaching a learner driver during an automatic driving lesson with S-UK Driving School in West London" className="w-full h-[clamp(20rem,70vw,26.25rem)] lg:h-[540px] object-cover" loading="eager" fetchpriority="high" width="940" height="540" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>

          <motion.div
            className="absolute -left-4 sm:-left-8 bottom-8 card-soft p-5 w-56 animate-float"
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}
          >
            <p className="text-4xl font-heading font-extrabold brand-text">98%</p>
            <p className="text-sm text-neutral-500 font-medium">Pass Rate</p>
          </motion.div>

          <motion.div
            className="absolute -right-3 top-8 card-soft px-4 py-3 flex items-center gap-2"
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}
          >
            <ShieldCheck size={22} className="text-green-600" />
            <div>
              <p className="text-sm font-bold leading-none">DVSA</p>
              <p className="text-xs text-neutral-500">Approved</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
